import Vue from "vue";
import postUserMixin from "@/v2/mixins/post-users.js";
import catalogMixin from "@/v2/mixins/catalog";
import productMixin from "@/v2/mixins/products.js";
import warehouseInventoryMixin from "@/v2/mixins/warehouse-inventories.js";
import boDatatableMixin from "@/v2/mixins/bo-datatable.js";
import httpMixin from "@/v2/mixins/http.js";
import { createInventoryGate } from "js/frontend/utils/inventory-gate.js";
import { relatedBundlesMixin } from "js/frontend/mixins/related-bundles.js";

Vue.component("bo-catalog-list", require("js/frontend/components/bo-catalog-list.vue").default);
Vue.component("bo-mobile-catalog-list", require("js/frontend/components/bo-mobile-catalog-list.vue").default);
Vue.component("bo-return-popup", require("js/frontend/components/return/bo-return-popup.vue").default);
Vue.component("bo-catalog-quick-add-popup", require("js/frontend/components/bo-catalog-quick-add-popup.vue").default);
Vue.component("bo-related-bundle-modal", require("js/frontend/components/bo-related-bundle-modal.vue").default);
Vue.component("bo-mobile-category", require("js/frontend/components/bo-mobile-category.vue").default);
Vue.component("bo-snackbar", require("@/v2/components/bo-snackbar.vue").default);
Vue.component("bo-login-popup", require("js/frontend/modules/bo-login-popup.vue").default);
Vue.component("bo-announcement-popup", require("js/frontend/components/bo-announcement-popup.vue").default);
Vue.component("bo-announcement-details", require("js/frontend/components/bo-announcement-details.vue").default);

const app = new Vue({
    el: "#app",
    mixins: [
        postUserMixin,
        catalogMixin,
        productMixin,
        warehouseInventoryMixin,
        boDatatableMixin,
        httpMixin,
        relatedBundlesMixin,
    ],
    data() {
        return {
            showLoginPopup: false,
            catalogMode: window.bo.catalogMode,
            catalogUrl: window.bo.catalogsUrl,
            salesReturnValidationMode: window.bo.salesReturnValidationMode,
            returnReasonOptions: window.bo.returnReasonOptions,
            returnReasonLabels: window.bo.returnReasonLabels,
            isBatchEnable: window.bo.isBatchEnable,
            isSerialNumberEnable: window.bo.isSerialNumberEnable,
            cartId: window.bo.cartId,
            cartOutletId: window.bo.cartOutletId,
            outletId: window.bo.outletId,
            companyId: window.bo.companyId,
            selectedProduct: null,
            showPopup: false,
            showQuickAddPopup: false,
            modal_active: false,
            loginpop_active: false,
            currentUserWarehouseInventoriesUrl: window.bo.currentUserWarehouseInventoriesUrl,
            user_warehouse_inventories: {},
            // #111887: gates the stock badges while the inventories request for
            // the current product list is in flight, and invalidates stale
            // callbacks from superseded requests.
            inventoryGate: createInventoryGate(),
            productData: {},
            listLoading: false, // #111741: dims the rendered list while a new list/stock loads (replaces the full-screen veil here)
            categoryData: [],
            categoriesLoaded: false,
            selectedProductCategoryId: null,
            catalogPageNumber: 1,
            isSearching: window.bo.isSearching,
            generalTranslations: window.bo.generalTranslations,
            catalogTranslations: window.bo.catalogTranslations,
            currencySymbol: window.bo.currencySymbol,
            lineItemMaxDecimalPlace: window.bo.lineItemMaxDecimalPlace,
            isMobileViewport:
                // Follows $breakpoint-tablet-vertical from _variables.scss
                typeof window !== "undefined" && typeof window.matchMedia === "function"
                    ? window.matchMedia("(max-width: 768px)").matches
                    : window.innerWidth <= 768,
            mobileMediaQuery: null,
            // #111877: in-page category picker overlay (mobile only)
            showMobileCategoryPicker: false,
            // announcement
            activeAnnouncement: true,
            featuredPosts: window.bo.featuredPosts,
            currentIndex: 0,
            userId: window.bo.userId,
            checkSession: true,
        };
    },
    mounted() {
        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
            // Follows $breakpoint-tablet-vertical from _variables.scss
            this.mobileMediaQuery = window.matchMedia("(max-width: 768px)");
            this.isMobileViewport = this.mobileMediaQuery.matches;

            if (typeof this.mobileMediaQuery.addEventListener === "function") {
                this.mobileMediaQuery.addEventListener("change", this.handleViewportMediaChange);
            } else if (typeof this.mobileMediaQuery.addListener === "function") {
                this.mobileMediaQuery.addListener(this.handleViewportMediaChange);
            }
        }

        if (this.userId) {
            this.checkSession = true;
        } else {
            const stored = sessionStorage.getItem("checkSession");
            this.checkSession = stored ? JSON.parse(stored) : true;
        }

        if (this.isSearching) {
            this.refreshInventories(window.bo.productData);
        } else {
            let currentUrl = window.location.href;
            let text = currentUrl.substr(currentUrl.indexOf("category/") + 9);
            let textId = text.split("#")[0];
            this.selectedProductCategoryId = parseInt(textId);

            this.getPageNumber(currentUrl);
            this.loadProducts();
        }

        this.loadCatalogs();
    },
    beforeDestroy() {
        if (this.mobileMediaQuery) {
            if (typeof this.mobileMediaQuery.removeEventListener === "function") {
                this.mobileMediaQuery.removeEventListener("change", this.handleViewportMediaChange);
            } else if (typeof this.mobileMediaQuery.removeListener === "function") {
                this.mobileMediaQuery.removeListener(this.handleViewportMediaChange);
            }
        }
    },
    computed: {
        inventoriesLoaded() {
            return this.inventoryGate.loaded;
        },
        posts() {
            return this.featuredPosts?.[0] ?? null;
        },
        currentContent() {
            return this.posts?.promptDetails?.[this.currentIndex] ?? null;
        },
    },
    watch: {
        activeAnnouncement(newVal, oldVal) {
            if (oldVal === true && newVal === false) {
                this.savePostBeforeNavigate();
            }
        },
    },
    methods: {
        handleViewportMediaChange(event) {
            this.isMobileViewport = event.matches;
        },

        // "Available in a bundle deal": list every bundle containing this product.
        openRelatedBundles(product) {
            this.showRelatedBundleModal = true;
            this.loadRelatedBundles(product.id);
        },

        closeRelatedBundles() {
            this.showRelatedBundleModal = false;
            this.relatedBundlesRequestId++; // drop any page still in flight
        },

        favoriteRelatedBundle(bundle) {
            bundle.favorited = !bundle.favorited;
            this.postData(
                window.bo.favoriteProductUrl,
                { product_id: bundle.id },
                (response) => {
                    this.showSnackbar(
                        response?.data ?? `<span style="color: #007AFF; font-weight: 600;">1</span> item(s) were updated.`
                    );
                },
                (failure) => {
                    bundle.favorited = !bundle.favorited;

                    if (failure?.response?.data?.errors?.code == "user_not_login") {
                        this.showLoginPopup = true;
                    } else {
                        this.showSnackbar(
                            "<span style='color:#FF0000';>" + failure?.response?.data?.message + "</span>",
                            true
                        );
                    }
                },
                false
            );
        },

        activeReturnPopup(product) {
            this.selectedProduct = product;
            this.showPopup = true;
        },

        deactivatePopup() {
            this.showPopup = false;
        },

        activeQuickAddPopup(product) {
            this.selectedProduct = product;
            this.showQuickAddPopup = true;
        },

        deactivateQuickAddPopup() {
            this.showQuickAddPopup = false;
        },

        // Swap modals rather than stacking them.
        openRelatedBundlesFromQuickAdd(product) {
            this.deactivateQuickAddPopup();
            this.openRelatedBundles(product);
        },

        // #111877: in-page picker open/close + selection (mobile)
        openMobileCategoryPicker() {
            this.showMobileCategoryPicker = true;
        },
        closeMobileCategoryPicker() {
            this.showMobileCategoryPicker = false;
        },
        onMobileCategorySelected(productCategoryId) {
            this.showMobileCategoryPicker = false;
            this.selectedProductCategory(productCategoryId);
        },

        selectedProductCategory(productCategoryId) {
            this.isSearching = false;
            this.selectedProductCategoryId = parseInt(productCategoryId);
            if (!window.EventBus) {
                window.EventBus = new Vue();
            }
            window.EventBus.$emit("category-id", this.selectedProductCategoryId);
            this.catalogPageNumber = 1;
            this.loadProducts();
        },

        selectedPageNumber(pageNumber) {
            this.catalogPageNumber = pageNumber;
            this.loadProducts();
        },

        loadCatalogs(payload, success, failure) {
            if (!payload) {
                payload = {};
            }

            payload.params = payload.params ?? {};
            payload.params.outletId = this.outletId;
            payload.params.isReturnMode = window.bo.catalogMode == "return-catalog";

            this.getData(
                window.bo.catalogCategoryUrl,
                payload,
                (response) => {
                    this.categoryData = [];
                    this.categoryData = response.data;
                    this.categoriesLoaded = true;
                    this.loadVirtualCategoryCounts(payload.params);
                    if (success) success(this.categoryData);
                },
                () => (this.categoriesLoaded = true),
                false
            ); // #111741: category tree is not worth a full-screen veil
        },

        loadVirtualCategoryCounts(params) {
            if (!this.categoryData.some((category) => category.id < 0)) {
                return;
            }

            this.getData(
                window.bo.virtualCategoryCountsUrl,
                { params },
                (response) => {
                    const counts = response.data ?? {};
                    this.categoryData = this.categoryData.map((category) =>
                        category.id < 0 && counts[category.id] !== undefined
                            ? { ...category, virtual_count: counts[category.id] }
                            : category
                    );
                },
                () => {},
                false
            );
        },

        loadProducts(payload, success, failure) {
            const listTicket = this.inventoryGate.close(); // #111887: supersede in-flight inventory requests
            this.listLoading = true; // #111741
            if (!payload) {
                payload = {};
            }

            payload.params = payload.params ?? {};
            payload.params.categoryId = this.selectedProductCategoryId;
            payload.params.page = this.catalogPageNumber;
            payload.params.outletId = this.outletId;
            payload.params.excludeHiddenProduct = true;

            let productUrl = window.bo.catalogProductsUrl;

            if (this.catalogMode === "return-catalog") {
                productUrl = window.bo.returnCatalogProductsUrl;
            }

            this.getData(
                productUrl,
                payload,
                (response) => {
                    if (!listTicket.isCurrent()) {
                        // #111741: without the blocking overlay a second category/page click can
                        // land while this list is in flight — an older list must not overwrite
                        // the newer one or push its stale URL.
                        return;
                    }
                    // The new list is shown by refreshInventories() once its stock is known, so
                    // cards and stock badges paint together (like SKU/name/price) instead of the
                    // badges popping in one request later. The old cards stay untouched until then.
                    this.refreshInventories(response.data, () => {
                        if (success) success(this.productData);
                        this.assignNewUrl();
                    });
                },
                (error) => {
                    // Products request failed: the currently rendered list stays as it is. If its
                    // badges are still hidden (commit-first list whose own stock request this
                    // close() superseded), re-sync its stock so they are not hidden for good.
                    if (listTicket.isCurrent() && !this.inventoryGate.loaded) {
                        this.refreshInventories(this.productData);
                    }
                    if (listTicket.isCurrent()) {
                        this.listLoading = false; // #111741
                    }
                    if (failure) failure(error);
                },
                false // #111741: catalog reads dim the list instead of raising the full-screen veil
            );
        },

        assignNewUrl() {
            let baseUrl = this.getBaseUrl();
            let newUrl =
                baseUrl != ""
                    ? baseUrl +
                      this.selectedProductCategoryId +
                      "?" +
                      //   "outletId=" +
                      //   this.outletId +
                      //   "&" +
                      //   "cartId=" +
                      //   this.cartId +
                      //   "&" +
                      "page=" +
                      this.catalogPageNumber
                    : "";

            if (newUrl != "") {
                window.history.pushState({}, "", newUrl);
            }
        },

        // Loads the stock for `pendingProductData` and shows that list.
        //
        // Desktop switch from an already rendered list: the new list is held back until its
        // stock is known, then list and stock map are committed in one render so the badges
        // never blink in after the cards — the current list stays on screen meanwhile.
        // Commit-first (list now, stock when it lands, as before) whenever there is nothing
        // to keep on screen: the first load and the search-results mount (a superseded
        // held-back first list would otherwise be lost if the replacing request failed),
        // mobile (no stock badge on the card) and Favourites (the list component filters
        // the still-rendered products by category id immediately).
        // Passing the list that is already on screen only re-syncs its stock (nothing is
        // re-committed). `onCommitted` runs once the list is on screen.
        refreshInventories(pendingProductData, onCommitted = null) {
            const commit = (inventories) => {
                this.listLoading = false; // #111741
                this.productData = {};
                this.productData = pendingProductData;
                if (inventories !== null) {
                    this.user_warehouse_inventories = inventories;
                }
                if (onCommitted) {
                    onCommitted();
                }
            };
            const isRenderedList = pendingProductData === this.productData;
            const hasRenderedList = (this.productData?.data?.length ?? 0) > 0;
            const waitForInventories =
                !isRenderedList && hasRenderedList && !this.isMobileViewport && Number(this.selectedProductCategoryId) !== -1;

            if (!waitForInventories && !isRenderedList) {
                this.inventoryGate.hide(); // #111887: stock unknown for the new list until it settles
                commit(null);
            }

            // Gather the inventories using the product_id and Assign to this.userWarehouseInventories
            if (pendingProductData?.data?.length > 0 && this.currentUserWarehouseInventoriesUrl) {
                const productIds = Object.values(pendingProductData.data).map((item) => item.id);
                const ticket = this.inventoryGate.begin(); // #111887
                this.getCurrentUserWarehouseInventories(
                    {
                        params: { product_id: productIds.join(",") },
                    },
                    (warehouseInventories) => {
                        if (!ticket.settle()) {
                            // #111887: a newer product list or inventory request
                            // superseded this one — its data must not be applied.
                            return;
                        }
                        // all bundle inventories
                        let tempBundleInventories = {};
                        const filteredBundleInventories = warehouseInventories.filter(function (item) {
                            const productType = item.product_type ?? item.product?.type;
                            return productType == "bundle";
                        });
                        this.boProcessRecords(tempBundleInventories, filteredBundleInventories, "product_id");

                        // all non bundle inventories
                        let tempWithoutBundleInventories = {};
                        const filteredWithoutBundleInventories = warehouseInventories.filter(function (item) {
                            const productType = item.product_type ?? item.product?.type;
                            const isMatrixProduct =
                                item.is_matrix_product ??
                                item.product?.parent?.bulk_variation_input?.bulk_variation_form ??
                                false;

                            return productType != "bundle" && !isMatrixProduct;
                        });
                        this.boProcessRecords(
                            tempWithoutBundleInventories,
                            filteredWithoutBundleInventories,
                            "product_parent_id"
                        );
                        let tempMatrixInventories = {};
                        const filteredMatrixProducts = warehouseInventories.filter(function (item) {
                            const productType = item.product_type ?? item.product?.type;
                            const isMatrixProduct =
                                item.is_matrix_product ??
                                item.product?.parent?.bulk_variation_input?.bulk_variation_form ??
                                false;

                            return productType != "bundle" && isMatrixProduct;
                        });
                        this.boProcessRecords(tempMatrixInventories, filteredMatrixProducts, "product_id");

                        const inventories = {
                            ...tempBundleInventories,
                            ...tempWithoutBundleInventories,
                            ...tempMatrixInventories,
                        };
                        if (waitForInventories) {
                            commit(inventories);
                        } else {
                            this.user_warehouse_inventories = inventories;
                        }
                    },
                    () => {
                        // #111887: request failed — if this request is still the
                        // current one, fall back to the pre-existing behaviour
                        // (badges render from the current map) rather than hiding
                        // stock badges forever; the held-back list must still show.
                        if (!ticket.settle()) {
                            return;
                        }
                        if (waitForInventories) {
                            commit(null);
                        }
                    },
                    false // #111741: catalog reads dim the list instead of raising the full-screen veil
                );
            } else {
                // #111887: nothing to fetch (no products, or no inventories URL) —
                // badges must not stay hidden and the list must still show.
                this.inventoryGate.release();
                if (waitForInventories) {
                    commit(null);
                }
            }
        },

        getPageNumber(url) {
            let pageParamIndex = url.indexOf("page=");
            if (pageParamIndex !== -1) {
                let pageNumber = parseInt(url.substr(pageParamIndex + 5), 10);

                if (!isNaN(pageNumber)) {
                    this.catalogPageNumber = pageNumber;
                }
            }
        },

        getBaseUrl() {
            let url = window.location.href;

            // Find the index of "/catalog/category/"
            let targetPath = "/catalog/category/";
            let index = url.indexOf(targetPath);

            if (index !== -1) {
                // Extract the base URL dynamically
                let baseUrl = url.substring(0, index + targetPath.length);
                return baseUrl;
            } else {
                targetPath = "/search";
                index = url.indexOf(targetPath);
                let baseUrl = url.substring(0, index);

                return baseUrl + "/category/";
            }
        },

        showSnackbar(message, isError) {
            // Show Snackbar
            this.$refs.bo_snackbar.showSnackbar(message, "", "", true, 3000, isError);
        },

        // announcement
        activateAnnouncement() {
            this.activeAnnouncement = true;
        },
        deactivateAnnouncement() {
            this.activeAnnouncement = false;
        },
        navigateDetailsPage() {
            this.savePostBeforeNavigate();
            const content = this.currentContent;
            if (!content) {
                window.location.href = this.posts.link;
                return;
            }

            window.location.href = `${this.posts.link}/${content.id}`;
        },
        savePostBeforeNavigate() {
            if (this.userId) {
                this.createPostUsers(
                    {
                        posts: this.posts?.promptDetails,
                        user_id: this.userId,
                    },
                    () => {},
                    null
                );
            } else {
                this.checkSession = false;
                sessionStorage.setItem("checkSession", JSON.stringify(this.checkSession));
            }
        },
        previousPage() {
            this.currentIndex--;
        },
        nextPage() {
            this.currentIndex++;
        },
    },
});
